import {Button, Checkbox, Radio} from "antd";
import {CloseOutlined} from "@ant-design/icons";
import {useMemo, useState} from "react";
import {useRecoilValue} from "recoil";
import {locationsState} from "../../../../state/atoms";
import {TeamDrawer} from "./TeamDrawer";
import {Preset} from "./permissionModel";
import {FILTER_PERMISSIONS, NO_FILTERS, UserFilters} from "./userAccess";

const LOCATIONS_PREVIEW_COUNT = 8;

interface UserFilterDrawerProps {
  filters: UserFilters;
  presets: Preset[];
  onApply: (filters: UserFilters) => void;
  onClose: () => void;
}

function toggled<T>(list: T[], value: T, checked: boolean): T[] {
  return checked ? [...list, value] : list.filter((it) => it !== value);
}

export function UserFilterDrawer({filters, presets, onApply, onClose}: UserFilterDrawerProps) {
  const locations = useRecoilValue(locationsState);
  const [draft, setDraft] = useState(filters);
  const [showAll, setShowAll] = useState(false);

  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    [locations]
  );
  const visibleLocations = showAll ? sortedLocations : sortedLocations.slice(0, LOCATIONS_PREVIEW_COUNT);

  return (
    <TeamDrawer label={"Filter Users"} className={"user-filter-drawer"} onClose={onClose}>
      {(close) => {
        const apply = (next: UserFilters) => {
          onApply(next);
          close();
        };

        return <>
          <div className={"td-header"}>
            <h4>Filter Users</h4>
            <button className={"td-close"} aria-label={"Close"} onClick={close}><CloseOutlined/></button>
          </div>

          <div className={"td-body user-filter-body"}>
            <section>
              <h5>Locations</h5>
              <Radio checked={draft.locationIds.length === 0}
                     onChange={() => setDraft({...draft, locationIds: []})}>All locations</Radio>
              {visibleLocations.map((location) =>
                <div key={location.internalId}>
                  <Checkbox
                    checked={draft.locationIds.includes(location.internalId!)}
                    onChange={(ev) => setDraft({
                      ...draft,
                      locationIds: toggled(draft.locationIds, location.internalId!, ev.target.checked)
                    })}
                  >{location.name}</Checkbox>
                </div>)}
              {!showAll && sortedLocations.length > LOCATIONS_PREVIEW_COUNT &&
                <button className={"link-button show-more"} onClick={() => setShowAll(true)}>Show more</button>}
            </section>

            <section>
              <h5>Permissions</h5>
              <Radio checked={draft.presetIds.length === 0 && draft.permissions.length === 0}
                     onChange={() => setDraft({...draft, presetIds: [], permissions: []})}>All Permissions</Radio>
              {presets.length > 0 && <h6>Presets</h6>}
              {presets.map((preset) =>
                <div key={preset.id}>
                  <Checkbox
                    checked={draft.presetIds.includes(preset.id)}
                    onChange={(ev) => setDraft({
                      ...draft,
                      presetIds: toggled(draft.presetIds, preset.id, ev.target.checked)
                    })}
                  >{preset.name}</Checkbox>
                </div>)}
              <h6>Individual permissions</h6>
              {FILTER_PERMISSIONS.map((permission) =>
                <div key={permission.key}>
                  <Checkbox
                    checked={draft.permissions.includes(permission.key)}
                    onChange={(ev) => setDraft({
                      ...draft,
                      permissions: toggled(draft.permissions, permission.key, ev.target.checked)
                    })}
                  >{permission.label}</Checkbox>
                </div>)}
            </section>
          </div>

          <div className={"td-footer"}>
            <Button onClick={() => apply(NO_FILTERS)}>Clear</Button>
            <Button type={"primary"} onClick={() => apply(draft)}>Apply</Button>
          </div>
        </>;
      }}
    </TeamDrawer>
  );
}
